/**
 * O espelho da ordenação de processos (TUT-204a).
 *
 * A lista canônica vive na API (`apps/api/src/domain/process-sorting.ts`);
 * aqui ela existe porque o web é pacote separado e não importa do servidor —
 * a mesma política dos outros tipos-espelho que a TUT-182 preservou de
 * propósito.
 *
 * **Espelho sem trava diverge**: é a família do descritor de status, onde o
 * web usa chave minúscula e a API maiúscula, e ninguém percebe até a tela
 * pedir algo que o servidor recusa. Por isso `process-sorting.test.ts` lê os
 * DOIS arquivos e reprova se as listas deixarem de bater.
 */

export const PROCESS_SORT_COLUMNS = [
  "number",
  "claimant",
  "company",
  "nextEvent",
  "status",
] as const;

export type ProcessSortColumn = (typeof PROCESS_SORT_COLUMNS)[number];
export type ProcessSortDirection = "asc" | "desc";

export function isProcessSortColumn(value: string): value is ProcessSortColumn {
  return (PROCESS_SORT_COLUMNS as readonly string[]).includes(value);
}

/**
 * O próximo estado do cabeçalho clicado.
 *
 * Três posições, e a terceira é a que costuma faltar: ascendente,
 * descendente e **sem ordenação** — voltar ao padrão do servidor tem de ser
 * alcançável pelo mesmo clique, senão a pessoa fica presa na ordem que
 * escolheu por engano.
 */
export function proximaOrdenacao(
  atual: { sort?: ProcessSortColumn; dir?: ProcessSortDirection },
  coluna: ProcessSortColumn,
): { sort?: ProcessSortColumn; dir?: ProcessSortDirection } {
  if (atual.sort !== coluna) return { sort: coluna, dir: "asc" };
  if (atual.dir === "asc") return { sort: coluna, dir: "desc" };
  return { sort: undefined, dir: undefined };
}

/**
 * TUT-204c — a `ariaSortDaColuna` que ficava aqui foi REMOVIDA, e o motivo
 * merece ficar escrito no lugar dela.
 *
 * Ela nasceu na TUT-204a como "o contrato de acessibilidade do cabeçalho" e
 * nunca teve chamador em produção: o `SortableHeader` recalculava a mesma
 * lógica inline, e só o teste desta pasta a exercitava. A medição de 22/08
 * registrou o achado como código morto.
 *
 * O conserto NÃO foi apagar e seguir. A regra é legítima e precisa existir em
 * algum lugar — o que estava errado era existir em DOIS, com um deles nunca
 * alcançado. Hoje ela vive em `list-sorting.ts`, é comum às dezesseis listas, e
 * o `SortableHeader` a CHAMA. Uma cópia a menos e um chamador a mais.
 */
